import { useState, useEffect } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useWizardStore, useWizardActions } from '@/stores';
import { MigrationSummary } from '../MigrationSummary';

export function ConfirmationStep() {
  const organizationData = useWizardStore((s) => s.organizationData);
  const selectedModules = useWizardStore((s) => s.selectedModules);
  const matchedModules = useWizardStore((s) => s.matchedModules);
  const bridgeEntity = useWizardStore((s) => s.bridgeEntity);
  const modulePropertyOverrides = useWizardStore((s) => s.modulePropertyOverrides);
  const { setIsValid } = useWizardActions();

  const [acknowledged, setAcknowledged] = useState(false);

  // Update validity based on acknowledgment
  useEffect(() => {
    setIsValid(acknowledged);
  }, [acknowledged, setIsValid]);

  if (!organizationData) {
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Missing Data</AlertTitle>
        <AlertDescription>
          Organization data is missing. Please go back and complete the previous steps.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium">Review Migration Configuration</h3>
        <p className="text-sm text-muted-foreground">
          Please review all details before starting the migration. This action cannot be undone.
        </p>
      </div>

      <MigrationSummary
        organizationData={organizationData}
        selectedModules={selectedModules}
        matchedModules={matchedModules}
        bridgeEntity={bridgeEntity}
        modulePropertyOverrides={modulePropertyOverrides}
      />

      {/* Warnings */}
      <Alert variant="warning">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Important Notice</AlertTitle>
        <AlertDescription className="space-y-2">
          <p>Before proceeding, please confirm:</p>
          <ul className="list-disc pl-4 space-y-1">
            <li>You have verified all source IDs are correct for each module being transitioned.</li>
            <li>You understand that user authentication and access management will transfer to the Amplify platform.</li>
            <li>You understand that selected modules will adopt the Amplify interface and design language.</li>
            <li>You are authorized to initiate this platform transition on behalf of the organization.</li>
          </ul>
        </AlertDescription>
      </Alert>

      {/* Terms Acknowledgment */}
      <div className="rounded-lg border p-4">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={acknowledged}
            onChange={(e) => setAcknowledged(e.target.checked)}
            className="mt-1 h-4 w-4 rounded border-input"
            aria-describedby="terms-description"
          />
          <div>
            <span className="font-medium">
              I confirm that I have reviewed all the information above and understand that this
              migration cannot be undone.
            </span>
            <p id="terms-description" className="text-sm text-muted-foreground mt-1">
              By checking this box and clicking "Start Migration", you authorize the transition of
              the selected modules to the Amplify platform, including the transfer of user
              authentication, access controls, and interface conversion.
            </p>
          </div>
        </label>
      </div>
    </div>
  );
}
